import { translate } from "@/i18n";
import { agentActivityUiMessages } from "@/i18n/messages/agent-activity-ui";
import { renderActivityLabel } from "@/lib/activity-label";
import { isDesktopInteractionTool } from "@/lib/desktop-interaction-cursor";
import { isCursorStatusContent } from "@/lib/work-toast-tool-labels";
import type { AgentCursor } from "@/stores/canvas-store";
import {
  agentCursorAnnotationTargets,
  cursorOwnsAnnotationCallout,
} from "@/lib/agent-annotation";

/** The cursor's tool step in the app language. Call at render time. */
export function cursorToolCallText(cursor: AgentCursor): string | undefined {
  return cursor.toolCall
    ? renderActivityLabel(cursor.toolCallLabel, cursor.toolCall)
    : undefined;
}

/** Cursor text as shown: the backend's "waking up..." status stays an
 *  identifier in state and reads in the app language here. */
export function cursorContentText(content: string): string {
  return isCursorStatusContent(content)
    ? translate(agentActivityUiMessages, "ui.waking_up")
    : content;
}

function calloutOwnsReply(cursor: AgentCursor): boolean {
  if (!cursor.executionId) return false;
  const normalize = (text: string) => text.trim().replace(/\s+/g, " ");
  const reply = normalize(cursor.contentFull ?? cursor.content ?? "");
  if (!reply) return false;
  const targets = agentCursorAnnotationTargets(cursor).filter(
    (target) => target.executionId === cursor.executionId,
  );
  // A later context callout can return ownership to a substantive final,
  // even after an earlier answer callout in this execution was delivered.
  for (let index = targets.length - 1; index >= 0; index -= 1) {
    const target = targets[index];
    const callout = target?.callout;
    if (callout?.role) {
      return (
        !target?.dismissed &&
        callout.role === "answer" &&
        callout.delivered === true &&
        !!callout.content.trim()
      );
    }
  }
  const callouts = targets
    // Explicit context leaves the final in charge; pending answer delivery
    // must not accidentally suppress the fallback through text matching.
    .filter((target) => !target.dismissed && target.callout?.role === undefined)
    .map((target) => normalize(target.callout?.content ?? ""))
    .filter(Boolean);
  return callouts.includes(reply) || callouts.join(" ") === reply;
}

/**
 * Project mutable runtime status into the status currently owned by the
 * cursor UI. Atomic presentation narration stays visible while tool and reply
 * updates continue accumulating underneath for restoration after clear.
 */
export function agentCursorDisplayStatus(
  cursor: AgentCursor,
  standaloneAnnotations = false,
) {
  const presentsNarration = !!(
    !cursor.question &&
    cursor.presentationDeliveryId &&
    cursor.pointTarget
  );
  const desktopTool = isDesktopInteractionTool(
    cursor.toolName,
    cursor.readsOnly,
  );
  const repeatsCallout =
    !presentsNarration &&
    !cursor.error &&
    !cursor.question &&
    calloutOwnsReply(cursor);
  // A desktop call's label names what it does ("Opening Spotify", "Pressing
  // Cmd+N"); a presented step shows its own words in its place.
  const toolCall = presentsNarration ? undefined : cursorToolCallText(cursor);
  const toolFailed = !toolCall ? undefined : cursor.toolFailed;
  const narrationInCallout = agentCursorAnnotationTargets(cursor).some(
    (target) =>
      (standaloneAnnotations || target.dismissed) &&
      target.displayId === cursor.pointTarget?.displayId &&
      cursor.presentationNarration?.trim() === target.callout?.content.trim() &&
      cursorOwnsAnnotationCallout(cursor, target),
  );
  const narration = narrationInCallout
    ? undefined
    : (cursor.presentationNarration ?? "");
  const content = presentsNarration
    ? narration
    : repeatsCallout
      ? undefined
      : cursor.content && cursorContentText(cursor.content);
  const contentFull = presentsNarration
    ? narration
    : repeatsCallout
      ? undefined
      : cursor.contentFull && cursorContentText(cursor.contentFull);
  const error = presentsNarration ? undefined : cursor.error;
  const question = presentsNarration ? undefined : cursor.question;
  return {
    toolCall,
    toolFailed,
    content,
    contentFull,
    error,
    question,
    // Presentation movement and drawing should stay cursor-only when the
    // step has no explicit narration. Graphical annotations are rendered by
    // AgentAnnotationOverlay and do not need a fallback thinking bubble.
    thinking:
      !presentsNarration &&
      !desktopTool &&
      !repeatsCallout &&
      !toolCall &&
      !content &&
      !error &&
      !question,
  };
}
